import { useCallback, useState } from "react"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { usePalletLabelPrinting } from "@/hooks/usePalletLabelPrinting"
import { useDeleteGoodsReceiptPallet } from "@/screens/WarehouseReceiving/hooks/useDeleteGoodsReceiptPallet"
import type { GoodsReceiptPalletActionState } from "@/screens/WarehouseReceiving/types"
import { GoodsReceiptPalletActionType } from "@/screens/WarehouseReceiving/types"

interface UseGoodsReceiptPalletActionsProps {
  onRefetch?: () => void
}

interface UseGoodsReceiptPalletActionsReturn {
  actionState: GoodsReceiptPalletActionState
  handleCreate: () => void
  handleLpnEdit: (pallet: GoodsReceiptPallet) => void
  handleDelete: (pallet: GoodsReceiptPallet) => void
  handleDeleteConfirm: () => Promise<void>
  handleDestinationOverride: (pallet: GoodsReceiptPallet) => void
  handleQrPrint: (pallet: GoodsReceiptPallet) => void
  handleQrPrintAll: (pallets: GoodsReceiptPallet[]) => Promise<void>
  handleActionSuccess: () => void
  handleActionClose: () => void
}

export const useGoodsReceiptPalletActions = ({
  onRefetch,
}: UseGoodsReceiptPalletActionsProps = {}): UseGoodsReceiptPalletActionsReturn => {
  const [actionState, setActionState] = useState<GoodsReceiptPalletActionState>(null)
  const { print } = usePalletLabelPrinting()
  const { deletePallet } = useDeleteGoodsReceiptPallet({ onSuccess: onRefetch })

  const handleQrPrint = useCallback(
    (pallet: GoodsReceiptPallet) => {
      print(pallet.referenceId)
    },
    [print]
  )

  const handleQrPrintAll = useCallback(
    async (pallets: GoodsReceiptPallet[]) => {
      const sortedPallets = [...pallets].sort((a, b) => {
        return a.referenceId - b.referenceId
      })

      for (const pallet of sortedPallets) {
        const success = await print(pallet.referenceId)
        if (!success) {
          return
        }
      }
    },
    [print]
  )

  const handleActionClose = useCallback(() => {
    setActionState(null)
  }, [])

  const handleLpnEdit = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.EditLpn, pallet })
  }, [])

  const handleDelete = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.Delete, pallet })
  }, [])

  const handleDestinationOverride = useCallback((pallet: GoodsReceiptPallet) => {
    setActionState({ type: GoodsReceiptPalletActionType.EditDestination, pallet })
  }, [])

  const handleCreate = useCallback(() => {
    setActionState({ type: GoodsReceiptPalletActionType.Create })
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (actionState?.type !== GoodsReceiptPalletActionType.Delete) {
      return
    }

    await deletePallet(actionState.pallet.goodsReceiptPalletId)
    setActionState(null)
  }, [actionState, deletePallet])

  const handleActionSuccess = useCallback(() => {
    setActionState(null)
    onRefetch?.()
  }, [onRefetch])

  return {
    actionState,
    handleCreate,
    handleLpnEdit,
    handleDelete,
    handleDeleteConfirm,
    handleDestinationOverride,
    handleQrPrint,
    handleQrPrintAll,
    handleActionSuccess,
    handleActionClose,
  }
}
